/**
 * Image for TV listings (channel icon, programme icon).
 * - If listing has no image (src missing/empty): show InsideAccess logo.
 * - If listing references an image: try to load it; on failure show InsideAccess logo.
 * Optional: set VITE_USE_IMAGE_PROXY=true and deploy image-proxy with --no-verify-jwt to avoid 404s in console.
 */
import { useState } from 'react'
import { getProxiedImageUrl } from '@/lib/imageProxy'
import TvFallbackLogo from './TvFallbackLogo'

interface TvListingImageProps {
  /** Image URL from listing; omit or empty → show InsideAccess logo */
  src?: string | null
  alt: string
  width: number
  height: number
  style?: React.CSSProperties
  objectFit?: 'contain' | 'cover'
}

export default function TvListingImage({
  src,
  alt,
  width,
  height,
  style = {},
  objectFit = 'contain',
}: TvListingImageProps) {
  const [loadError, setLoadError] = useState(false)
  const hasUrl = typeof src === 'string' && src.trim() !== ''

  if (!hasUrl || loadError) {
    return (
      <TvFallbackLogo
        width={width}
        height={height}
        style={style}
      />
    )
  }

  const useProxy = import.meta.env.VITE_USE_IMAGE_PROXY === 'true'
  const imgSrc = useProxy ? getProxiedImageUrl(src) : src

  return (
    <img
      src={imgSrc}
      alt={alt}
      width={width}
      height={height}
      style={{ objectFit, ...style }}
      onError={() => setLoadError(true)}
    />
  )
}
